import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { ChangePasswordScreen } from "@/components/ChangePasswordScreen";
import { OnboardingChecklist } from "@/components/Onboarding/OnboardingChecklist";
import { Sidebar } from "@/components/Layout/Sidebar";
import { Header } from "@/components/Dashboard/Header";

// Dashboard
import { CommandCentreDashboard } from "@/components/Dashboard/CommandCentreDashboard";
import { AssociationDashboard } from "@/components/Association/AssociationDashboard";

// Social Hub service
import { SocialHubPage } from "@/components/SocialHub/SocialHubPage";
import { PostsPage } from "@/components/Posts/PostsPage";
import { SocialAnalyticsPage } from "@/components/Analytics/SocialAnalyticsPage";
import { ContentCalendarPage } from "@/components/Calendar/ContentCalendarPage";
import { MediaLibraryPage } from "@/components/Media/MediaLibraryPage";
import { CampaignsPage } from "@/components/Campaigns/CampaignsPage";
import { ApprovalsPage } from "@/components/Approvals/ApprovalsPage";
import { WorkflowsPage } from "@/components/Approvals/WorkflowsPage";
import { IntelligencePage } from "@/components/Intelligence/IntelligencePage";
import { AIAssistantPage } from "@/components/AI/AIAssistantPage";
import { SocialDMsPage } from "@/components/SocialDMs/SocialDMsPage";
import { SocialAccountsPage } from "@/components/SocialAccounts/SocialAccountsPage";

// Press & Journalist service
import MediaRegistry from "@/pages/MediaRegistry";
import { AccreditationView } from "@/components/Accreditation/AccreditationView";
import { EventsView } from "@/components/Events/EventsView";
import { PressReleaseView } from "@/components/PressReleases/PressReleaseView";
import { PressReviewView } from "@/components/Accreditation/PressReviewView";
import { JournalistDirectoryView } from "@/components/Accreditation/JournalistDirectoryView";
import { AwardsView } from "@/components/Awards/AwardsView";
import { MediaKitsView } from "@/components/MediaKits/MediaKitsView";

// Analytics service
import { CoverageAnalyticsPage } from "@/components/Analytics/CoverageAnalyticsPage";
import { JournalistAnalyticsPage } from "@/components/Analytics/JournalistAnalyticsPage";

// Organisation & settings
import Organization from "@/pages/Organization";
import { OrgUsersView } from "@/components/Organisation/OrgUsersView";
import { OrgSettingsView } from "@/components/Settings/OrgSettingsView";
import Settings from "@/pages/Settings";

// Common
import { InstitutionSupportView } from "@/components/InstitutionSupport/InstitutionSupportView";
import { TasksPage } from "@/components/Tasks/TasksPage";
import Billing from "@/pages/Billing";

function ViewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-8 w-28" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
    </div>
  );
}

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveViewState] = useState(() => searchParams.get('view') || 'dashboard');
  const { orgId, orgType } = useOrgPermissions();
  const queryClient = useQueryClient();

  const setActiveView = (view: string) => {
    setActiveViewState(view);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (view === 'dashboard') next.delete('view');
      else next.set('view', view);
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    const view = searchParams.get('view');
    if (view && view !== activeView) setActiveViewState(view);
  }, [searchParams]);

  const { data: needsPasswordChange } = useQuery({
    queryKey: ['requires_password_change'],
    queryFn: async () => {
      const { data } = await supabase.rpc('requires_password_change' as any)
      return data as boolean
    }
  });

  const { data: orgBrand } = useQuery({
    queryKey: ['org-brand', orgId],
    enabled: !!orgId && !needsPasswordChange,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations')
        .select('brand_primary, brand_secondary, brand_accent, name')
        .eq('id', orgId!)
        .single() as any)
      return data as { brand_primary: string; brand_secondary: string; brand_accent: string; name: string } | null
    }
  });

  useEffect(() => {
    if (!orgBrand) return
    const root = document.documentElement
    root.style.setProperty('--color-primary', orgBrand.brand_primary || '#006B3C')
    root.style.setProperty('--color-accent',  orgBrand.brand_accent  || '#C8A84B')
    root.style.setProperty('--color-header',  orgBrand.brand_primary || '#0D2240')
  }, [orgBrand]);

  if (needsPasswordChange) {
    return (
      <ChangePasswordScreen
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['requires_password_change'] })}
      />
    );
  }

  const renderView = () => {
    switch (activeView) {
      // ── Dashboard ───────────────────────────────────────────────────────────
      case 'dashboard':
        if (orgType === 'association') return <AssociationDashboard />;
        return <CommandCentreDashboard onNavigate={setActiveView} />;

      // Association-only views
      case 'assoc-members':
      case 'assoc-membership':
        return <AssociationDashboard />;

      // ── Social Hub service ───────────────────────────────────────────────
      case 'social-hub':
        return <SocialHubPage />;

      case 'posts':
        return <PostsPage />;

      case 'social-analytics':
        return <SocialAnalyticsPage />;

      case 'calendar':
        return <ContentCalendarPage onSchedulePost={() => setActiveView('posts')} />;

      case 'media-library':
        return <MediaLibraryPage />;

      case 'campaigns':
        return <CampaignsPage />;

      case 'approvals':
        return <ApprovalsPage />;

      case 'workflows':
        return <WorkflowsPage />;

      case 'social-listening':
      case 'intelligence':
        return <IntelligencePage />;

      case 'ai-assistant':
        return <AIAssistantPage onSendToPost={() => setActiveView('posts')} />;

      case 'social-dms':
        return <SocialDMsPage />;

      case 'social-accounts':
        return <SocialAccountsPage />;

      // ── Press & Journalist service ───────────────────────────────────────
      case 'media-registry':
        return <MediaRegistry />;

      case 'accreditation':
        return <AccreditationView />;

      case 'events':
        return <EventsView />;

      case 'press-releases':
        return <PressReleaseView />;

      case 'press-review':
        return <PressReviewView />;

      case 'journalist-directory':
        return <JournalistDirectoryView />;

      case 'awards':
        return <AwardsView />;

      case 'media-kits':
        return <MediaKitsView />;

      // ── Analytics service ────────────────────────────────────────────────
      case 'analytics':
        if (orgType === 'association' || orgType === 'individual') return <JournalistAnalyticsPage />;
        return <CoverageAnalyticsPage />;

      case 'journalist-analytics':
        return <JournalistAnalyticsPage />;

      // ── Organisation & settings ──────────────────────────────────────────
      case 'organization':
        return <Organization />;

      case 'org-users':
        return <OrgUsersView />;

      case 'org-settings':
        return <OrgSettingsView />;

      case 'settings':
        return <Settings />;

      // ── Common ───────────────────────────────────────────────────────────
      case 'support':
        return <InstitutionSupportView />;

      case 'tasks':
        return <TasksPage />;

      case 'billing':
        return <Billing />;

      default:
        return <CommandCentreDashboard onNavigate={setActiveView} />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar activeView={activeView} onViewChange={setActiveView} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <main className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <Suspense fallback={<ViewSkeleton />}>
              {renderView()}
            </Suspense>
          </div>
        </main>
      </div>

      <OnboardingChecklist onNavigate={setActiveView} />
    </div>
  );
};

export default Index;
